import React from "react";
import { AlertTriangle, CheckCircle2, Clapperboard, X, Lock, ShieldCheck, Info } from "lucide-react";
import { MediaFrame } from "@/components/common/MediaFrame";
import { formatResolutionLabel, isKlingPro } from "@/domain/resolutionLabels";
import { getModelSceneCapabilities, parseScenesFromPrompt } from "@/domain/sceneEngine";

export const VideoPreflightModal = ({
  open,
  onClose,
  onConfirm,
  model,
  videoMode = "image_to_video",
  startAsset = null,
  endAsset = null,
  motionVideoAsset = null,
  duration = 5,
  resolution = "720P",
  aspectRatio = "9:16",
  audio = false,
  cost = null,
  reserve = null,
  multishot = false,
  shots = [],
  prompt = "",
  isSubmitting = false,
  maxChars = null,
}) => {
  if (!open) return null;

  const hasStartMention = /@startframe\b/i.test(prompt || "");
  const hasEndMention = /@endframe\b/i.test(prompt || "");

  const isI2V = videoMode === "image_to_video";
  const isFLF = videoMode === "first_last_frame";
  const isT2V = videoMode === "text_to_video";
  const isMotionControl = videoMode === "motion_control";

  const missingStart = (isI2V || isFLF || isMotionControl) && !startAsset;
  const missingEnd = isFLF && !endAsset;
  const missingMotion = isMotionControl && !motionVideoAsset;

  const effectiveMaxChars = maxChars || model?.maxPromptChars || (model?.id?.startsWith("kling") ? 2500 : null);
  const promptLength = (prompt || "").length;
  const isPromptTooLong = effectiveMaxChars ? promptLength > effectiveMaxChars : false;

  const parsedScenes = parseScenesFromPrompt(prompt);
  const hasScenes = parsedScenes.hasScenes;
  const hasScenesOrMultishot = multishot || hasScenes;
  const detectedScenes = hasScenes ? parsedScenes.scenes : (shots || []);
  const sceneCaps = getModelSceneCapabilities(model?.id);
  const sceneStrategy = sceneCaps?.strategy || "structured";
  const sceneLabel = sceneCaps?.label || "Escenas estructuradas (APIMart)";
  const maxCharsPerScene = sceneCaps?.maxPromptPerScene || 512;

  const totalShotDuration = hasScenesOrMultishot
    ? detectedScenes.reduce((acc, s) => acc + (Number(s.duration) || 0), 0)
    : duration;
  const shotMismatch = hasScenesOrMultishot && totalShotDuration !== duration;
  const shotTooLong = hasScenesOrMultishot && detectedScenes.some((s) => (s.prompt || "").length > maxCharsPerScene);
  const shotEmpty = hasScenesOrMultishot && (detectedScenes.length === 0 || detectedScenes.some((s) => !(s.prompt || "").trim()));
  const multishotFlfConflict = hasScenesOrMultishot && (isFLF || Boolean(endAsset));
  const audioSupported = Boolean(model?.audioSupported ?? model?.audio_supported);
  const audioIncompatibleWith = model?.audioIncompatibleWith || model?.audio_incompatible_with || [];
  const isAudioIncompatible = Boolean(audio && audioIncompatibleWith.includes(videoMode));
  const audioSurcharge = Number(model?.audioSurchargeRateUsd ?? model?.audio_surcharge_rate_usd ?? 0);
  const hasAudioSurcharge = audioSurcharge > 0;
  const audioSurchargeTotal = duration * audioSurcharge;

  const isBlocked = missingStart || missingEnd || missingMotion || isPromptTooLong || shotMismatch || shotTooLong || shotEmpty || multishotFlfConflict || isAudioIncompatible;

  const mentionMismatch = isT2V && hasStartMention;

  return (
    <div
      data-testid="video-preflight-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in"
    >
      <div className="relative w-full max-w-lg rounded-xl border border-[#383838] bg-[#141414] p-5 shadow-2xl">
        {/* Cabecera */}
        <div className="flex items-center justify-between pb-3 border-b border-[#262626]">
          <div className="flex items-center gap-2">
            <Clapperboard size={18} className="text-[#f5b544]" />
            <h3 className="text-sm font-semibold text-[#ededed]">
              Confirmación antes de generar vídeo
            </h3>
          </div>
          <button
            type="button"
            data-testid="preflight-close-btn"
            onClick={onClose}
            disabled={isSubmitting}
            className="text-[#737373] hover:text-[#d4d4d4]"
          >
            <X size={16} />
          </button>
        </div>

        {/* Resumen compacto */}
        <div className="mt-4 flex flex-col gap-3.5 text-xs">
          {/* Modelo y modo */}
          <div className="grid grid-cols-2 gap-2 rounded-lg bg-[#1a1a1a] p-3 border border-[#2a2a2a]">
            <div>
              <span className="text-[10px] uppercase font-mono text-[#737373]">Modelo:</span>
              <div className="font-semibold text-[#ededed] truncate">
                {model?.label || model?.name || model?.id || "Modelo de vídeo"}
              </div>
              <div className="text-[10px] font-mono text-[#8a8a8a]">
                {model?.gateway_model_id || model?.id}
              </div>
            </div>
            <div>
              <span className="text-[10px] uppercase font-mono text-[#737373]">Modalidad efectiva:</span>
              <div className="font-semibold">
                {isI2V ? (
                  <span className="text-emerald-400">Image-to-Video (I2V)</span>
                ) : isFLF ? (
                  <span className="text-indigo-400">First & Last Frame (FLF)</span>
                ) : (
                  <span className="text-amber-400">Text-to-Video (T2V)</span>
                )}
              </div>
              <div className="text-[10px] text-[#8a8a8a]">
                {isT2V ? "Generación solo por texto" : "Generación guiada por imagen"}
              </div>
            </div>
          </div>

          {/* Fotograma inicial (Start Frame) */}
          <div className="flex flex-col gap-1.5 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] p-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#a3a3a3]">
                Start Frame (Fotograma Inicial)
              </span>
              {startAsset ? (
                <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-400">
                  <CheckCircle2 size={11} /> Vinculado a APIMart
                </span>
              ) : isT2V ? (
                <span className="rounded bg-[#262626] px-2 py-0.5 text-[10px] text-[#8a8a8a]">
                  No aplica en T2V
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded bg-red-500/10 px-2 py-0.5 text-[10px] font-medium text-red-400">
                  <AlertTriangle size={11} /> Obligatorio ausente
                </span>
              )}
            </div>

            {startAsset ? (
              <div className="mt-1 flex items-center gap-3">
                <div className="relative h-14 w-20 flex-shrink-0 overflow-hidden rounded border border-[#333] bg-black">
                  <MediaFrame asset={startAsset} className="h-full w-full" fit="cover" />
                </div>
                <div className="flex min-w-0 flex-1 flex-col text-[11px]">
                  <span className="truncate font-medium text-[#ededed]">
                    {startAsset.label || startAsset.filename || "Imagen inicial"}
                  </span>
                  <span className="font-mono text-[10px] text-[#737373] truncate">
                    source_asset_id: {startAsset.id}
                  </span>
                  <span className="text-[10px] text-emerald-400/90">
                    Se enviará como URL de CDN en el campo <code className="font-mono">first_frame_image</code>
                  </span>
                </div>
              </div>
            ) : isT2V ? (
              <p className="mt-1 text-[11px] text-[#737373]">
                Esta tarea se enviará como generación basada exclusivamente en texto. APIMart no recibirá ninguna imagen inicial.
              </p>
            ) : (
              <p className="mt-1 text-[11px] text-red-400">
                La modalidad seleccionada exige un Start Frame. Selecciona o conecta una imagen para poder continuar.
              </p>
            )}
          </div>

          {/* Fotograma final (End Frame) si corresponde */}
          {isFLF && (
            <div className="flex flex-col gap-1.5 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] p-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#a3a3a3]">
                  End Frame (Fotograma Final)
                </span>
                {endAsset ? (
                  <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-400">
                    <CheckCircle2 size={11} /> Vinculado a APIMart
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded bg-red-500/10 px-2 py-0.5 text-[10px] font-medium text-red-400">
                    <AlertTriangle size={11} /> Obligatorio ausente
                  </span>
                )}
              </div>
              {endAsset ? (
                <div className="mt-1 flex items-center gap-3">
                  <div className="relative h-14 w-20 flex-shrink-0 overflow-hidden rounded border border-[#333] bg-black">
                    <MediaFrame asset={endAsset} className="h-full w-full" fit="cover" />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col text-[11px]">
                    <span className="truncate font-medium text-[#ededed]">
                      {endAsset.label || endAsset.filename || "Imagen final"}
                    </span>
                    <span className="font-mono text-[10px] text-[#737373] truncate">
                      end_asset_id: {endAsset.id}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="mt-1 text-[11px] text-red-400">
                  La modalidad First & Last Frame exige un fotograma final válido.
                </p>
              )}
            </div>
          )}

          {/* Vídeo de movimiento si corresponde a Motion Control */}
          {isMotionControl && (
            <div className="flex flex-col gap-1.5 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] p-3">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#a3a3a3]">
                  Vídeo de referencia de movimiento (Motion Control)
                </span>
                {motionVideoAsset ? (
                  <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-400">
                    <CheckCircle2 size={11} /> Vinculado a APIMart
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded bg-red-500/10 px-2 py-0.5 text-[10px] font-medium text-red-400">
                    <AlertTriangle size={11} /> Obligatorio ausente
                  </span>
                )}
              </div>
              {motionVideoAsset ? (
                <div className="mt-1 flex items-center gap-3">
                  <div className="relative h-14 w-20 flex-shrink-0 overflow-hidden rounded border border-[#333] bg-black">
                    <MediaFrame asset={motionVideoAsset} className="h-full w-full" fit="cover" />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col text-[11px]">
                    <span className="truncate font-medium text-[#ededed]">
                      {motionVideoAsset.label || motionVideoAsset.filename || "Vídeo de movimiento"}
                    </span>
                    <span className="font-mono text-[10px] text-[#737373] truncate">
                      motion_video_asset_id: {motionVideoAsset.id}
                    </span>
                    <span className="text-[10px] text-emerald-400/90">
                      Se enviará como URL de CDN en el campo <code className="font-mono">video_urls</code>
                    </span>
                  </div>
                </div>
              ) : (
                <p className="mt-1 text-[11px] text-red-400">
                  La modalidad Motion Control exige seleccionar un vídeo de referencia de movimiento.
                </p>
              )}
            </div>
          )}

          {/* Secuencia cinematográfica de escenas si corresponde */}
          {hasScenesOrMultishot && (
            <div data-testid="preflight-multishot-section" className="flex flex-col gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-amber-400">
                    Secuencia de Escenas ({detectedScenes.length} escenas)
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                    sceneStrategy === "structured"
                      ? "bg-sky-500/20 text-sky-400 border border-sky-500/30"
                      : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                  }`}>
                    {sceneLabel}
                  </span>
                </div>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${shotMismatch ? "bg-red-500/20 text-red-300 font-bold" : "bg-emerald-500/10 text-emerald-400"}`}>
                  {totalShotDuration}s / {duration}s total
                </span>
              </div>

              {shotMismatch && (
                <div data-testid="preflight-multishot-mismatch-error" className="text-[11px] text-red-400 font-medium">
                  ⚠️ Error: La suma de duraciones de las escenas ({totalShotDuration}s) no coincide con la duración total ({duration}s).
                </div>
              )}

              {multishotFlfConflict && (
                <div data-testid="preflight-multishot-flf-error" className="text-[11px] text-red-400 font-medium">
                  ⚠️ Incompatibilidad: Este modelo no admite secuencias de escenas junto con End Frame.
                </div>
              )}

              <div className="mt-1 space-y-1.5 max-h-36 overflow-y-auto pr-1">
                {detectedScenes.map((s, idx) => (
                  <div key={idx} className="flex items-start justify-between gap-2 rounded border border-[#2a2a2a] bg-[#141414] p-2 text-[11px]">
                    <span className="font-mono text-[#f5b544] whitespace-nowrap">
                      Escena {s.index || idx + 1} ({s.duration}s):
                    </span>
                    <span className="truncate flex-1 text-[#ededed] italic">
                      {s.prompt ? `"${s.prompt}"` : <span className="text-red-400 not-italic">(prompt vacío)</span>}
                    </span>
                    <span className="text-[10px] text-[#666] font-mono">
                      {(s.prompt || "").length}/{maxCharsPerScene}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Advertencia si prompt supera límite de caracteres */}
          {isPromptTooLong && (
            <div
              data-testid="preflight-prompt-length-error"
              className="rounded-lg border border-red-500/40 bg-red-500/10 p-2.5 text-xs text-red-300"
            >
              <div className="flex items-center gap-1.5 font-semibold text-red-400">
                <AlertTriangle size={13} />
                <span>Prompt excede el límite de longitud</span>
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-red-200/90">
                El prompt tiene {promptLength.toLocaleString()} caracteres y supera el límite máximo de {effectiveMaxChars.toLocaleString()} caracteres admitido por {model?.label || model?.id || "el modelo"}. Debes reducir el texto antes de generar.
              </p>
            </div>
          )}

          {/* Advertencia semántica si prompt dice @startframe pero modo es T2V */}
          {mentionMismatch && (
            <div
              data-testid="preflight-mention-warning"
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-300"
            >
              <div className="flex items-center gap-1.5 font-semibold">
                <AlertTriangle size={13} />
                <span>Advertencia de mención visual</span>
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-amber-200/90">
                El prompt incluye la mención <code className="font-mono font-bold">@startframe</code>, pero el modo actual es Texto a Vídeo (T2V). APIMart ignorará la referencia de imagen porque no se adjuntará ningún archivo. Si deseas que respete la imagen, cancela y activa el modo Image-to-Video.
              </p>
            </div>
          )}

          {/* Estado del audio nativo */}
          <div data-testid="preflight-audio-section" className="flex flex-col gap-1.5 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] p-3">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#a3a3a3]">
                Audio nativo
              </span>
              {!audioSupported ? (
                <span className="rounded bg-[#262626] px-2 py-0.5 text-[10px] text-[#8a8a8a]">
                  Sin audio nativo
                </span>
              ) : isAudioIncompatible ? (
                <span className="inline-flex items-center gap-1 rounded bg-red-500/10 px-2 py-0.5 text-[10px] font-medium text-red-400">
                  <AlertTriangle size={11} /> Incompatible con {isFLF ? "FLF" : videoMode}
                </span>
              ) : audio ? (
                <span className="inline-flex items-center gap-1 rounded bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-400">
                  <CheckCircle2 size={11} /> Audio activo
                </span>
              ) : (
                <span className="rounded bg-[#262626] px-2 py-0.5 text-[10px] text-[#8a8a8a]">
                  Vídeo silencioso
                </span>
              )}
            </div>

            {isAudioIncompatible ? (
              <div data-testid="preflight-audio-flf-error" className="mt-1 text-[11px] text-red-400 font-medium leading-relaxed">
                ⚠️ Incompatibilidad: {model?.label || model?.name || model?.id} no admite generación de audio junto con fotograma final ({isFLF ? "First & Last Frame" : videoMode}) según las restricciones contractuales del proveedor (máx. 1 imagen de referencia). Desactiva el audio para continuar.
              </div>
            ) : !audioSupported ? (
              <p className="mt-1 text-[11px] text-[#737373]">
                Este modelo no admite pista de audio nativo. El archivo generado será mudo.
              </p>
            ) : audio ? (
              <div className="mt-1 flex items-center justify-between text-[11px]">
                <span className="text-emerald-400/90">
                  🔊 Pista de audio sincrónica generada por IA.
                </span>
                <span className="text-[10px] font-mono text-[#a3a3a3]">
                  {hasAudioSurcharge ? `+${audioSurchargeTotal.toFixed(4)} USD ($${audioSurcharge.toFixed(4)}/s)` : "Incluido ($0 extra)"}
                </span>
              </div>
            ) : (
              <p className="mt-1 text-[11px] text-[#737373]">
                🔇 Generación sin audio. El vídeo se generará en modo silencioso.
              </p>
            )}
          </div>

          {/* Parámetros técnicos y coste */}
          {(() => {
            const isProChoice = isKlingPro(resolution, model);
            const formattedRes = formatResolutionLabel(resolution, model);
            return (
              <>
                {isProChoice && (
                  <div
                    data-testid="preflight-pro-pricing-notice"
                    className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 text-xs text-amber-300"
                  >
                    <div className="flex items-center gap-1.5 font-semibold text-amber-400">
                      <AlertTriangle size={13} />
                      <span>Tarifa de modalidad Professional (PRO) pendiente de confirmación</span>
                    </div>
                    <p className="mt-1 text-[11px] leading-relaxed text-amber-200/90">
                      El proveedor no publica una tarifa oficial confirmada para la modalidad PRO. Para no emitir una estimación engañosa, el coste real será liquidado y confirmado al finalizar la tarea.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-4 gap-2 text-[11px] text-[#8e8e8e] pt-1">
                  <div className="rounded bg-[#1a1a1a] p-2 border border-[#2a2a2a]">
                    <span className="text-[9px] uppercase font-mono text-[#666]">Duración:</span>
                    <div className="font-medium text-[#ededed] truncate">{duration} segundos {multishot ? `(${shots.length} tomas)` : ""}</div>
                  </div>
                  <div className="rounded bg-[#1a1a1a] p-2 border border-[#2a2a2a]">
                    <span className="text-[9px] uppercase font-mono text-[#666]">Resolución:</span>
                    <div className="font-medium text-[#ededed] truncate" title={formattedRes}>
                      <span>{formattedRes} · {aspectRatio}</span>
                      <span className="sr-only"> {resolution} · {aspectRatio}</span>
                    </div>
                  </div>
                  <div className="rounded bg-[#1a1a1a] p-2 border border-[#2a2a2a]">
                    <span className="text-[9px] uppercase font-mono text-[#666]">Coste est.:</span>
                    <div className="font-medium text-[#ededed]">
                      {isProChoice ? (
                        <span className="text-amber-300 text-[10px]">Tarifa no confirmada</span>
                      ) : cost != null ? (
                        `$${Number(cost).toFixed(4)} USD`
                      ) : (
                        "Tarifa std"
                      )}
                    </div>
                  </div>
                  <div className="rounded bg-[#1a1a1a] p-2 border border-[#2a2a2a]">
                    <span className="text-[9px] uppercase font-mono text-[#666]">Reserva prev.:</span>
                    <div className="font-medium text-[#f5b544]">
                      {isProChoice ? (
                        <span className="text-amber-300 text-[10px]">No confirmada</span>
                      ) : reserve != null ? (
                        `$${Number(reserve).toFixed(4)} USD`
                      ) : cost != null ? (
                        `$${(Number(cost) * 1.2).toFixed(4)} USD`
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </div>

        {/* Acciones */}
        <div className="mt-6 flex items-center justify-end gap-3 pt-3 border-t border-[#262626]">
          <button
            type="button"
            data-testid="preflight-cancel-btn"
            onClick={onClose}
            disabled={isSubmitting}
            className="v-btn-ghost v-btn-sm"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="preflight-confirm-btn"
            onClick={onConfirm}
            disabled={isBlocked || isSubmitting}
            className="v-btn-primary v-btn-sm inline-flex items-center gap-1.5"
            title={
              isPromptTooLong
                ? "El prompt supera el límite de caracteres admitido"
                : shotMismatch
                ? "La suma de duraciones de las tomas no coincide con la duración total"
                : isAudioIncompatible
                ? "El audio nativo es incompatible con la modalidad de vídeo en este proveedor"
                : isBlocked
                ? "Faltan recursos obligatorios para la modalidad seleccionada"
                : "Confirmar y enviar generación a APIMart"
            }
          >
            {isSubmitting ? (
              <span>Enviando…</span>
            ) : isPromptTooLong ? (
              <>
                <Lock size={12} />
                <span>Bloqueado (prompt excede límite)</span>
              </>
            ) : shotMismatch ? (
              <>
                <Lock size={12} />
                <span>Bloqueado (duración tomas errónea)</span>
              </>
            ) : isAudioIncompatible ? (
              <>
                <Lock size={12} />
                <span>Bloqueado (audio incompatible)</span>
              </>
            ) : isBlocked ? (
              <>
                <Lock size={12} />
                <span>{missingStart || missingEnd || missingMotion ? "Bloqueado (falta imagen)" : "Bloqueado (revisar configuración)"}</span>
              </>
            ) : (
              <>
                <ShieldCheck size={13} />
                <span>Confirmar y generar vídeo</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
